import { useCallback, useEffect, useRef, useState } from 'react'
import { serverClock, type Battle, type BattleRound } from '@mezha/api'
import { PLAYBACK_MS, shouldPlay } from './model'

export type Playback = { round: BattleRound; started: number; before?: Battle['actors'] }

export function useReducedCombatMotion(setting: boolean) {
  const [system, setSystem] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
  useEffect(() => {
    const q = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!q) return
    const change = () => setSystem(q.matches)
    q.addEventListener('change', change)
    return () => q.removeEventListener('change', change)
  }, [])
  return setting || system
}

/** Mounted once per battle. Initial history isn't a queue; a reconnect gap fast-forwards. */
export function usePlayback(b: Battle, reduced: boolean) {
  const previousActors = useRef(b.actors)
  useEffect(() => { previousActors.current = b.actors }, [b.actors])
  const last = b.log.at(-1)
  const latest = last?.round ?? b.round - 1
  const [state, setState] = useState<{ seen: number; play: Playback | null }>(() => ({ seen: latest, play: null }))
  let current = state
  // Derive before committing: the final snapshot must not flash the result and unmount the scene.
  if (latest > state.seen) {
    const remaining = b.deadline_at ? Date.parse(b.deadline_at) - serverClock.now() : Infinity
    current = { seen: latest, play: shouldPlay(state.seen, last, reduced, document.hidden, remaining) && last ? { round: last, started: performance.now(), before: previousActors.current } : null }
    setState(current)
  }
  const skip = useCallback(() => setState(s => s.play ? { ...s, play: null } : s), [])
  const play = reduced ? null : current.play
  useEffect(() => {
    if (!play) { if (reduced) skip(); return }
    const timer = window.setTimeout(skip, Math.max(0, PLAYBACK_MS - (performance.now() - play.started)))
    const visibility = () => { if (document.hidden) skip() }
    document.addEventListener('visibilitychange', visibility)
    return () => { window.clearTimeout(timer); document.removeEventListener('visibilitychange', visibility) }
  }, [play, reduced, skip])
  return { play, skip }
}
